import React, { useState } from 'react';
import { ShoppingCart, FileText, Tag, Info } from 'lucide-react';
import type { CatalogProduct } from '../types/catalog'; // Importar CatalogProduct
import { Link, useNavigate } from 'react-router-dom';
import { IMAGES } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { STORAGE_CONFIG, getPublicImageUrl } from '../config/storage';
import { ProductImage } from './ProductImage';
import { toast } from 'react-toastify';
import { FEATURE_FLAGS } from '../config/featureFlags';
import ProductTags from './ProductTags';

interface ProductCardProps {
    product: CatalogProduct; // Cambiar a CatalogProduct
    onAddToCart: (product: CatalogProduct) => void; // Cambiar a CatalogProduct
    showAddToCart: boolean;
    size?: 'S' | 'M' | 'L';
}

const ProductCard: React.FC<ProductCardProps> = ({ 
    product, 
    onAddToCart, 
    showAddToCart,
    size = 'L'
}) => {
  const sizeClasses = {
    S: {
      card: 'p-1.5',
      content: 'pt-1',
      image: 'h-16',
      category: 'text-[9px]',
      title: 'text-[10px] font-medium line-clamp-1',
      price: 'text-[10px] font-bold',
      button: 'h-7 px-1 text-xs',
      buttonIcon: true,
      quantity: 'w-7 h-7 text-[10px] px-0.5',
      tags: 'mb-0.5',
      properties: 'mb-0.5 text-[9px]'
    },
    M: {
      card: 'p-2.5',
      content: 'pt-1.5',
      image: 'h-32',
      category: 'text-xs',
      title: 'text-xs sm:text-sm font-medium line-clamp-2',
      price: 'text-base font-bold',
      button: 'h-8 px-2 text-xs',
      buttonIcon: false,
      quantity: 'w-10 h-8 text-xs px-1',
      tags: 'mb-1.5',
      properties: 'mb-1.5 text-xs'
    },
    L: {
      card: 'p-3',
      content: 'pt-2',
      image: 'h-48',
      category: 'text-xs',
      title: 'text-sm font-medium line-clamp-2',
      price: 'text-lg sm:text-xl font-bold',
      button: 'h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm',
      buttonIcon: false,
      quantity: 'w-10 sm:w-12 h-8 sm:h-9 text-xs sm:text-sm px-1',
      tags: 'mb-2',
      properties: 'mb-2'
    }
  };

  const classes = sizeClasses[size];

  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const isPricingActive = FEATURE_FLAGS.SHOW_CART_PRICING && (!FEATURE_FLAGS.REQUIRE_AUTH_FOR_PRICES || isAuthenticated);
  // Desestructurar campos de CatalogProduct.
  // 'category' aquí se refiere a la categoría principal para visualización.
  // 'image_url' es el campo principal para la imagen. 'imageUrl' (lowercase L) también está disponible si se necesita.
  const { id, name, description, price, category, stock, image_url, brand } = product;
  const [imgSrc, setImgSrc] = useState<string>(() => {
    if (!image_url) return IMAGES.DEFAULT_PRODUCT; // Usar image_url
    
    if (image_url.startsWith('http') || image_url.startsWith('/')) { // Usar image_url
      return image_url;
    }
    
    try {
      return getPublicImageUrl(image_url); // Usar image_url
    } catch {
      return IMAGES.DEFAULT_PRODUCT;
    }
  });
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const handleImageError = () => {
        console.warn(`Failed to load image for product ${product.id}`);
        setImgSrc(IMAGES.DEFAULT_PRODUCT);
    };

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuantity = parseInt(event.target.value, 10);
        setQuantity(isNaN(newQuantity) || newQuantity < 1 ? 1 : newQuantity);
    };

    const handleCardClick = (e: React.MouseEvent) => {
        if (
            !(e.target as HTMLElement).closest('input') && 
            !(e.target as HTMLElement).closest('button')
        ) {
            navigate(`/product/${id}`);
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        setTooltipPosition({
            x: e.clientX + 10,
            y: e.clientY + 10
        });
    };

    return (
        <div
            onClick={handleCardClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onMouseMove={handleMouseMove}
            className={`group bg-white rounded-xl border border-gray-200/80 hover:border-amber-400/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col ${classes.card}`}
        >
            {/* Tooltip */}
            {showTooltip && product.description && (
                <div 
                    className="fixed z-50 bg-gray-900 text-white p-2.5 rounded-lg text-xs max-w-xs shadow-xl pointer-events-none backdrop-blur-sm bg-gray-900/95 border border-gray-700"
                    style={{
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                    }}
                >
                    {product.description}
                </div>
            )}

            {/* Imagen con aspect ratio fijo */}
            <Link to={`/product/${id}`} className="block aspect-square overflow-hidden rounded-t-lg bg-gray-50 relative">
                <div className="relative w-full h-full overflow-hidden">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                            <div className="animate-pulse w-full h-full bg-gray-200" />
                        </div>
                    )}
                    <img
                        src={imgSrc}
                        alt={name}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                            isLoading ? 'opacity-0' : 'opacity-100'
                        }`}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        loading="lazy"
                    />
                    {/* Tag de la marca */}
                    {brand && size !== 'S' && (
                        <div className="absolute top-2 right-2 bg-slate-900/75 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full backdrop-blur-md shadow-sm border border-white/10">
                            {brand}
                        </div>
                    )}
                </div>
            </Link>
            
            <div className={`flex flex-col flex-grow ${classes.content}`}>
                {/* Contenido */}
                <div className="flex-grow">
                    {/* Badge de Categoría */}
                    {category && size !== 'S' && (
                        <div className="mb-1.5">
                            <span className="inline-flex items-center text-[10px] sm:text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-full tracking-wide">
                                {category}
                            </span>
                        </div>
                    )}
                    
                    <h3 className={`text-gray-900 font-semibold group-hover:text-amber-600 transition-colors ${size === 'S' ? 'mb-0.5' : 'mb-1.5'} ${classes.title}`}>
                      {name}
                    </h3>

                    {/* Badge de Talles Disponibles */}
                    {product.availableSizes && product.availableSizes.length > 0 && size !== 'S' && (
                        <div className="flex items-center gap-1 mb-2 flex-wrap">
                            <span className="text-[10px] font-medium text-gray-500">Talles:</span>
                            <div className="flex flex-wrap gap-1">
                                {product.availableSizes.slice(0, 6).map(s => (
                                    <span key={s} className="text-[10px] font-semibold bg-amber-50 text-amber-800 px-1.5 py-0.2 rounded border border-amber-200/60">
                                        {s}
                                    </span>
                                ))}
                                {product.availableSizes.length > 6 && (
                                    <span className="text-[10px] font-medium text-amber-600">
                                        +{product.availableSizes.length - 6}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Chips de Industrias */}
                    {product.industries && product.industries.length > 0 && size !== 'S' && (
                        <div className="flex flex-wrap items-center gap-1 mb-2">
                        {product.industries.slice(0, 2).map((ind, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100/80 text-slate-600 border border-slate-200/70"
                          >
                            {ind}
                          </span>
                        ))}
                        {product.industries.length > 2 && (
                          <span className="text-[10px] font-medium text-slate-400 px-1">
                            +{product.industries.length - 2}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Tags del producto */}
                    {product.tags && product.tags.length > 0 && size !== 'S' && (
                      <div className={classes.tags}>
                        <ProductTags
                          tags={product.tags}
                          maxVisible={size === 'S' ? 2 : 3}
                          size={size === 'S' ? 'sm' : 'sm'}
                          variant="filled"
                          interactive={false}
                        />
                      </div>
                    )}

                    {/* Propiedades destacadas */}
                    {product.properties && Object.keys(product.properties).length > 0 && size !== 'S' && (
                      <div className={classes.properties}>
                        <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                          <Info className="w-3 h-3" />
                          <span>Características</span>
                        </div>
                        <div className="text-xs text-gray-700 space-y-1">
                          {Object.entries(product.properties).slice(0, size === 'M' ? 1 : 2).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium">
                                {Array.isArray(value) ? value.join(', ') : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Precio solo si está habilitado y autenticado */}
                    {isPricingActive && size !== 'S' && (
                      <p className={`text-amber-600 font-bold ${classes.price}`}>
                        ${price}
                      </p>
                    )}
                  </div>

                {/* Controles siempre al fondo */}
                <div className={`flex items-center pt-2 mt-auto border-t border-gray-100 w-full min-w-0 ${size === 'S' ? 'pt-1 gap-1 justify-between' : 'gap-1.5'}`}>
                    <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        className={`border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-center font-medium shrink-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${classes.quantity}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (FEATURE_FLAGS.ALLOW_GUEST_CART || isAuthenticated) {
                                onAddToCart(product, quantity);
                                toast.success(isPricingActive ? 'Producto añadido al pedido' : 'Producto añadido a la cotización');
                            } else {
                                toast.error('Debes iniciar sesión para agregar productos');
                            }
                        }}
                        className={`${size === 'S' ? 'w-7 h-7 shrink-0' : 'flex-1 min-w-0'} bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-1 shadow-sm hover:shadow ${classes.button}`}
                    >
                        {isPricingActive ? (
                            <ShoppingCart className={size === 'S' ? 'w-3 h-3' : 'w-3.5 h-3.5 shrink-0'} />
                        ) : (
                            <FileText className={size === 'S' ? 'w-3 h-3' : 'w-3.5 h-3.5 shrink-0'} />
                        )}
                        {!classes.buttonIcon && (
                          <span className="truncate">{isPricingActive ? 'Añadir' : 'Cotizar'}</span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
